import React from 'react'
import styled, { keyframes } from 'styled-components'

interface SkeletonProps {
  height?: string | number
  width?: string | number
  borderRadius?: string | number
  className?: string
  style?: React.CSSProperties
}

interface SkeletonGridProps {
  children: React.ReactNode
  gap?: string | number
  columns?: number
  className?: string
}

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`

const shimmer = keyframes`
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
`

const SkeletonContainer = styled.div<{
  $height: string
  $width: string
  $borderRadius: string
}>`
  position: relative;
  min-height: ${props => props.$height};
  width: ${props => props.$width};
  border-radius: ${props => props.$borderRadius};
  background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  overflow: hidden;
  animation: ${pulse} 1.5s ease-in-out infinite;
`

const SkeletonShimmer = styled.div`
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: ${shimmer} 1s ease-in-out infinite;
`

const GridContainer = styled.div<{
  $gap: string
  $columns: number
}>`
  display: grid;
  gap: ${props => props.$gap};
  grid-template-columns: repeat(${props => props.$columns}, 1fr);
  width: 100%;
`

export const Skeleton: React.FC<SkeletonProps> = ({
  height = '20px',
  width = '100%',
  borderRadius = '8px',
  className = '',
  style = {},
}) => {
  const heightValue = typeof height === 'number' ? `${height}px` : height
  const widthValue = typeof width === 'number' ? `${width}px` : width
  const borderRadiusValue = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius

  return (
    <SkeletonContainer
      className={className}
      $height={heightValue}
      $width={widthValue}
      $borderRadius={borderRadiusValue}
      style={style}
    >
      <SkeletonShimmer />
    </SkeletonContainer>
  )
}

export const SkeletonGrid: React.FC<SkeletonGridProps> = ({
  children,
  gap = '16px',
  columns = 1,
  className = '',
}) => {
  const gapValue = typeof gap === 'number' ? `${gap}px` : gap

  return (
    <GridContainer className={className} $gap={gapValue} $columns={columns}>
      {children}
    </GridContainer>
  )
}
